import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../helpers/api";

// Effective permissions for the signed-in account, always read from the API (never cached locally),
// so the UI only shows what the server will accept.
const AccessContext = createContext(null);

export const AccessProvider = ({ children }) => {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest("/api/auth/access");
      setAccess(result.access);
    } catch (requestError) {
      setError(requestError.message || "Unable to load your permissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => {
    const granted = new Set(access?.permissions || []);
    const can = (...permissions) => Boolean(access?.isSuperAdmin || permissions.some((permission) => granted.has(permission)));
    return { access, loading, error, reload, can };
  }, [access, loading, error, reload]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

// Outside a provider (isolated component tests) nothing is hidden; the server still enforces every action.
const unrestricted = { access: null, loading: false, error: "", reload: () => {}, can: () => true };

export const useAccess = () => useContext(AccessContext) || unrestricted;

// Renders children only when the account holds any of the given permissions.
export const Can = ({ permission, children, fallback = null }) => {
  const { can } = useAccess();
  const permissions = Array.isArray(permission) ? permission : [permission];
  return can(...permissions) ? children : fallback;
};

export const AccessDenied = ({ hasOtherPages }) => (
  <div className="access-state access-denied" role="alert">
    <i className="ti ti-lock" aria-hidden="true" />
    <h1>You don't have access to this page</h1>
    <p>{hasOtherPages ? "Your role doesn't include this section. Ask an administrator to update your role." : "Your role doesn't include any sections yet. Ask an administrator to assign permissions."}</p>
  </div>
);

export const AccessLoadError = ({ error, onRetry }) => (
  <div className="access-state access-denied" role="alert">
    <i className="ti ti-alert-circle" aria-hidden="true" />
    <h1>We couldn't verify your access</h1>
    <p>{error}</p>
    <button type="button" className="access-retry" onClick={onRetry}>Try again</button>
  </div>
);
